import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Form, Formik, FormikHelpers } from "formik";
import { t } from "ttag";
import { Button, ChevronLeft, IconButton, TextField } from "@coolremote/shared";
import { createLoginValidationSchema } from "../../../components/UnifiedAcceptInvite/validationSchemas";
import {
  DeviceRegistrationLocationState,
  isValidDeviceRegistrationState
} from "../deviceRegistrationLocationState";
import { ExistingUserFlowState } from "./existingUserFlowState";
import { login, verifyTwoFactorCode } from "./existingUserFlowApi";
import TwoFactorDialog from "./TwoFactorDialog";
import parentStyles from "../DeviceRegistrationPublic.module.css";
import styles from "./ExistingUserFlow.module.css";

const validationSchema = createLoginValidationSchema(undefined);

interface LoginFormValues {
  username: string;
  password: string;
}

/**
 * Step 1 of the existing-user branch: log in with an existing
 * account. Reuses `createLoginValidationSchema`'s validation logic (called
 * with no `inviteInfo`, so `subscriptionCode` stays optional) — but not
 * `ExistingUserForm`/`AuthForm` themselves, since those render `@material-ui`
 * fields and this flow is `@coolremote/shared` + CSS Modules only.
 */
const LoginStep: React.FC = () => {
  const history = useHistory<DeviceRegistrationLocationState | undefined>();
  const deviceState = history.location.state;
  const [loginError, setLoginError] = useState<string | undefined>(undefined);
  const [pendingTwoFactorUsername, setPendingTwoFactorUsername] = useState<string | undefined>(undefined);
  const [twoFactorError, setTwoFactorError] = useState<string | undefined>(undefined);
  const [verifyingTwoFactor, setVerifyingTwoFactor] = useState(false);

  useEffect(() => {
    if (!isValidDeviceRegistrationState(deviceState)) {
      history.replace("/register-device");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isValidDeviceRegistrationState(deviceState)) {
    return null;
  }

  const handleBack = () => {
    history.goBack();
  };

  // `login`/`verifyTwoFactorCode` set the SDK's auth token, which is all the
  // rest of this wizard's own API calls need. Deliberately NOT calling the
  // global `setLoggedIn` action here — that also flips on the app's normal
  // post-login bootstrap (Routes.tsx fetches the user's full tree and shows
  // a blocking full-screen loader while it does), which hijacks this
  // still-mid-wizard screen. The product intent (see CongratulationsStep) is
  // for the technician to log in fresh through the real login screen once
  // the wizard finishes anyway.
  const proceedToSelectCustomer = (userId: string, hasProfessionalAccess: boolean) => {
    history.push("/register-device/existing-user/select-customer", {
      ...deviceState,
      userId,
      hasProfessionalAccess
    } as ExistingUserFlowState);
  };

  const handleSubmit = async (
    values: LoginFormValues,
    { setSubmitting }: FormikHelpers<LoginFormValues>
  ) => {
    setLoginError(undefined);
    try {
      const result = await login(values.username, values.password);
      if ("is2FA" in result) {
        setPendingTwoFactorUsername(values.username);
        setSubmitting(false);
        return;
      }
      proceedToSelectCustomer(result.userId, result.hasProfessionalAccess);
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : t`Incorrect username or password`);
      setSubmitting(false);
    }
  };

  const handleTwoFactorConfirm = async (code: string) => {
    if (!pendingTwoFactorUsername) {
      return;
    }
    setTwoFactorError(undefined);
    setVerifyingTwoFactor(true);
    try {
      const { userId, hasProfessionalAccess } = await verifyTwoFactorCode(pendingTwoFactorUsername, code);
      proceedToSelectCustomer(userId, hasProfessionalAccess);
    } catch (err) {
      setTwoFactorError(err instanceof Error ? err.message : t`The code you entered is invalid or expired`);
      setVerifyingTwoFactor(false);
    }
  };

  const handleTwoFactorCancel = () => {
    setPendingTwoFactorUsername(undefined);
    setTwoFactorError(undefined);
  };

  return (
    <div className={parentStyles.screen}>
      <div className={parentStyles.appBar}>
        <IconButton className={parentStyles.backButton} onClick={handleBack} aria-label={t`Back`}>
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={parentStyles.appBarTitle}>{t`Log In`}</span>
        <span />
      </div>

      <div className={styles.stepContent}>
        <Formik<LoginFormValues>
          initialValues={{ username: "", password: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => {
            const handleUsernameChange = (value: string) => setFieldValue("username", value);
            const handlePasswordChange = (value: string) => setFieldValue("password", value);

            return (
              <Form className={styles.form}>
                {loginError && <div className={styles.errorBanner}>{loginError}</div>}
                <TextField
                  label={t`Username`}
                  name="username"
                  fullWidth
                  autoComplete="username"
                  value={values.username}
                  onChange={handleUsernameChange}
                  error={touched.username ? errors.username : undefined}
                />
                <TextField
                  label={t`Password`}
                  name="password"
                  type="password"
                  fullWidth
                  autoComplete="current-password"
                  value={values.password}
                  onChange={handlePasswordChange}
                  error={touched.password ? errors.password : undefined}
                />
                <Button
                  type="submit"
                  variant="contained"
                  className={styles.primaryButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t`Logging in...` : t`LOGIN`}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </div>

      <TwoFactorDialog
        open={!!pendingTwoFactorUsername}
        submitting={verifyingTwoFactor}
        errorText={twoFactorError}
        onConfirm={handleTwoFactorConfirm}
        onCancel={handleTwoFactorCancel}
      />
    </div>
  );
};

export default LoginStep;
